import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import './ZaraTabs.css';

const ZaraTabs = ({
  refNextBack,
  data,
  currentVisibleSlide,
  setCurrentVisibleSlide,
  instanceId,
  panelId,
}) => {
  const fallbackId = data[0]?.id;
  const activeTab = data.some(({ id }) => id === currentVisibleSlide)
    ? currentVisibleSlide
    : fallbackId;
  const tabRefs = useRef([]);
  const tabsContainerRef = useRef(null);

  const [isDown, setIsDown] = useState(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const isDragging = useRef(false); // Track if dragging occurred

  const handleTabClick = (id, index) => {
    if (isDragging.current) return; // Prevent click during dragging

    setCurrentVisibleSlide(id);

    if (tabRefs.current[index]) {
      tabRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  useEffect(() => {
    if (fallbackId !== undefined && activeTab !== currentVisibleSlide) {
      setCurrentVisibleSlide(fallbackId);
    }
  }, [activeTab, currentVisibleSlide, fallbackId, setCurrentVisibleSlide]);

  const handleKeyDown = (event, index) => {
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = Math.min(index + 1, data.length - 1);
    if (event.key === 'ArrowLeft') nextIndex = Math.max(index - 1, 0);
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = data.length - 1;
    if (nextIndex === index) return;
    event.preventDefault();
    handleTabClick(data[nextIndex].id, nextIndex);
    tabRefs.current[nextIndex]?.focus();
  };

  const handleMouseDown = (e) => {
    setIsDown(true);
    isDragging.current = false; // Reset dragging flag
    tabsContainerRef.current.classList.add('active');
    startX.current = e.pageX - tabsContainerRef.current.offsetLeft;
    scrollLeft.current = tabsContainerRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    setIsDown(false);
    tabsContainerRef.current.classList.remove('active');
  };

  const handleMouseUp = () => {
    setIsDown(false);
    tabsContainerRef.current.classList.remove('active');
  };

  const handleMouseMove = (e) => {
    if (!isDown) return;

    e.preventDefault();
    isDragging.current = true;
    const x = e.pageX - tabsContainerRef.current.offsetLeft;
    const walk = (x - startX.current) * 1;
    tabsContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  useImperativeHandle(
    refNextBack,
    () => ({
      goToNextSlide() {
        const currentIndex = data.findIndex((item) => item.id === activeTab);
        if (currentIndex < data.length - 1) {
          handleTabClick(data[currentIndex + 1].id, currentIndex + 1);
        }
      },
      goToPrevSlide() {
        const currentIndex = data.findIndex((item) => item.id === activeTab);
        if (currentIndex > 0) {
          handleTabClick(data[currentIndex - 1].id, currentIndex - 1);
        }
      },
    }),
    [activeTab, data]
  );

  useEffect(() => {
    const container = tabsContainerRef.current;

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDown]);

  useEffect(() => {
    const container = document.querySelector('.zara-tabs-container');
    if (container) {
      container.addEventListener('touchmove', (e) => e.stopPropagation(), { passive: false });
    }
  }, []);

  return (
    <div className="zara-tabs-container" ref={tabsContainerRef}>
      <div className="zara-tabs" role="tablist" aria-label="Product information">
        {data.map(({ text, id }, index) => (
          <button
            key={id}
            ref={(el) => (tabRefs.current[index] = el)}
            className={`zara-tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => handleTabClick(id, index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            tabIndex={activeTab === id ? 0 : -1}
            aria-controls={panelId}
            id={`${instanceId}-tab-${id}`}
          >
            <div>{text}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ZaraTabs;
